import { heicTo, isHeic } from "heic-to";
import { useEffect, useState } from "react";

import { changeFileExtension } from "@/lib/helpers/changeFileExtension";

type UseHeicConversionProps = {
  file: Nullable<File>;
  quality?: number;
};

type ConversionState = {
  convertedFile: Nullable<File>;
  error: null | string;
  isConverting: boolean;
};

export function useHeicConversion({ file, quality = 0.85 }: UseHeicConversionProps) {
  const [conversionState, setConversionState] = useState<ConversionState>({
    convertedFile: null,
    error: null,
    isConverting: false,
  });

  useEffect(() => {
    // Reset state when no file
    if (!file) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setConversionState({
        convertedFile: null,
        error: null,
        isConverting: false,
      });

      return;
    }

    const convertHeicFile = async () => {
      setConversionState((prev) => ({ ...prev, error: null, isConverting: true }));

      try {
        const heicCheck = await isHeic(file);

        if (!heicCheck) {
          // Not a HEIC file, no conversion needed
          setConversionState({
            convertedFile: null,
            error: null,
            isConverting: false,
          });

          return;
        }

        const convertedFilename = changeFileExtension(file.name, "jpg");
        const convertedBlob = await heicTo({
          blob: file,
          quality,
          type: "image/jpeg",
        });

        const resultFile = new File([convertedBlob], convertedFilename, {
          lastModified: Date.now(),
          type: "image/jpeg",
        });

        // Batch the state updates together to prevent intermediate renders
        setConversionState({
          convertedFile: resultFile,
          error: null,
          isConverting: false,
        });
      } catch (err) {
        setConversionState({
          convertedFile: null,
          error: err instanceof Error ? err.message : "HEIC conversion failed",
          isConverting: false,
        });
      }
    };

    convertHeicFile();
  }, [file, quality]);

  return {
    convertedFile: conversionState.convertedFile,
    error: conversionState.error,
    isConverting: conversionState.isConverting,
  };
}
